export type NavChild = {
  id: string
  label: string
  /** Real app path — omit or set disabled when no page yet */
  path?: string
  /** Dimmed, not clickable (no real route / placeholder only) */
  disabled?: boolean
}

export type NavItem = {
  id: string
  label: string
  path: string
  match: (pathname: string) => boolean
  children?: NavChild[]
  /** Top-level dimmed entry (no destination) */
  disabled?: boolean
}

/**
 * Menu chuẩn (docs/ui-reference README §5 + chốt A7 + mở lại Sản xuất Đợt A2).
 * URL trang thật không đổi — chỉ đổi cây menu.
 * Mở lại menu cấp 1 "Sản xuất" với mục con "Ghi nhận sản lượng" (/production/record).
 */
export const NAV_ITEMS: NavItem[] = [
  {
    id: 'home',
    label: 'MENU',
    path: '/',
    match: (p) => p === '/',
  },
  {
    id: 'catalog',
    label: 'Danh mục',
    path: '/catalog',
    match: (p) =>
      p.startsWith('/catalog') ||
      p.startsWith('/products') ||
      p.startsWith('/purchasing/materials') ||
      p.startsWith('/purchasing/boms') ||
      p.startsWith('/purchasing/packaging-boms'),
    children: [
      { id: 'cat-products', label: 'Sản phẩm', path: '/products' },
      { id: 'cat-variant', label: 'Variant / Màu', path: '/catalog' },
      { id: 'cat-bom-aggregate', label: 'Tổng hợp Định mức', path: '/catalog/bom-aggregate' },
    ],
  },
  {
    id: 'sales',
    label: 'Sale',
    path: '/sales',
    match: (p) => p.startsWith('/sales'),
    children: [
      { id: 'sales-orders', label: 'Đơn hàng', path: '/sales/orders' },
      { id: 'sales-customers', label: 'Khách hàng', path: '/sales/customers' },
    ],
  },
  {
    id: 'planning',
    label: 'Kế hoạch',
    path: '/planning',
    match: (p) =>
      p.startsWith('/planning') ||
      p.startsWith('/purchasing/demand') ||
      p.startsWith('/purchasing/explosions'),
    children: [
      {
        id: 'plan-production-orders',
        label: 'Lệnh sản xuất',
        path: '/planning/production-orders',
      },
      {
        id: 'plan-mrp',
        label: 'Cân đối vật tư (MRP)',
        path: '/planning/mrp',
      },
      {
        id: 'plan-demand',
        label: 'Nhu cầu vật tư',
        path: '/purchasing/demand',
      },
      {
        id: 'plan-explosion',
        label: 'Gross Explosion',
        path: '/purchasing/explosions',
      },
      { id: 'plan-change', label: 'Phân tích thay đổi', disabled: true },
    ],
  },
  {
    id: 'production',
    label: 'Sản xuất',
    path: '/production/progress',
    match: (p) => p.startsWith('/production'),
    children: [
      {
        id: 'prod-progress',
        label: 'Tiến độ toàn xưởng',
        path: '/production/progress',
      },
      {
        id: 'prod-record',
        label: 'Ghi nhận sản lượng',
        path: '/production/record',
      },
    ],
  },
  {
    id: 'purchasing',
    label: 'Mua hàng',
    path: '/purchasing',
    match: (p) =>
      p.startsWith('/purchasing') &&
      !p.startsWith('/purchasing/demand') &&
      !p.startsWith('/purchasing/explosions') &&
      !p.startsWith('/purchasing/materials') &&
      !p.startsWith('/purchasing/boms') &&
      !p.startsWith('/purchasing/packaging-boms'),
    children: [
      { id: 'pur-overview', label: 'Tổng quan', path: '/purchasing' },
      { id: 'pur-po', label: 'Đơn mua hàng', path: '/purchasing/po' },
      {
        id: 'pur-receipt',
        label: 'Nhập hàng',
        path: '/warehouse/receipts',
      },
      {
        id: 'pur-sup',
        label: 'Nhà cung cấp',
        path: '/purchasing/suppliers',
      },
      {
        id: 'pur-standardization',
        label: 'Chuẩn hóa dữ liệu',
        path: '/purchasing/data-standardization',
      },
      // Ẩn "Cách tính giá" khỏi menu (trang vẫn giữ trong router để hoàn tác khi cần).
    ],
  },
  {
    id: 'warehouse',
    label: 'Kho',
    path: '/warehouse',
    match: (p) => p.startsWith('/warehouse'),
    children: [
      { id: 'wh-stock', label: 'Tồn kho', path: '/warehouse/stock' },
      { id: 'wh-issue-slips', label: 'Phiếu xuất kho', path: '/warehouse/issue-slips' },
      { id: 'wh-in', label: 'Nhập hàng', path: '/warehouse/receipts' },
      { id: 'wh-out', label: 'Xuất hàng', path: '/warehouse/issues' },
      { id: 'wh-hold', label: 'Giữ hàng', path: '/warehouse/reservations' },
      {
        id: 'wh-tx',
        label: 'Lịch sử biến động',
        path: '/warehouse/transactions',
      },
      { id: 'wh-stocktakes', label: 'Lịch sử kiểm kê', path: '/warehouse/stocktakes' },
    ],
  },
  {
    id: 'reports',
    label: 'Báo cáo',
    path: '/reports',
    match: (p) => p.startsWith('/reports'),
  },
  {
    id: 'admin',
    label: 'Quản trị',
    path: '/admin/users',
    match: (p) => p.startsWith('/admin'),
    disabled: false,
  },
]

/** Home module cards — chỉ mục có trang thật (không gồm mục mờ / ẩn). */
export const HOME_MODULES = NAV_ITEMS.filter(
  (item) =>
    item.id !== 'home' &&
    !item.disabled &&
    (item.children?.some((c) => c.path && !c.disabled) ||
      (!item.children && item.path && !item.disabled)),
)
